import { Env } from 'application/Shared/Utils';

import { WorkatoConnection, WorkatoStatus } from './types';

/**
 * Determines the Workato connection status for a tenant app
 *
 * Connection states (tested with GWS):
 * 1. New connection with any user:
 *    - authorized_at: timestamp
 *    - authorization_status: success
 *    - authorization_error: null
 *    - connection_lost_at: null
 *    - connection_lost_reason: null
 *
 * 2. Recipe run with unauthorized user:
 *    - authorized_at: timestamp
 *    - authorization_status: "success"
 *    - authorization_error: null
 *    - connection_lost_at: timestamp
 *    - connection_lost_reason: "403 forbidden"
 *
 * 3. Disconnected unauthorized user:
 *    - authorized_at: null
 *    - authorization_status: null
 *    - authorization_error: null
 *    - connection_lost_at: timestamp
 *    - connection_lost_reason: "403 forbidden"
 *
 * 4. Exception status:
 *    - authorized_at: timestamp
 *    - authorization_status: "exception"
 *    - authorization_error: "401 Unauthorized"
 *    - connection_lost_at: timestamp
 *    - connection_lost_reason: "401 Unauthorized"
 *
 * @param {WorkatoConnection} workatoConnection - The workato connection object
 * @returns {WorkatoStatus} The current status of the Workato connection
 */
export const getWorkatoStatus = (
    workatoConnection?: WorkatoConnection
): WorkatoStatus => {
    // Check if workatoConnection is empty object
    if (!workatoConnection || Object.keys(workatoConnection).length === 0)
        return WorkatoStatus.unavailable;

    if (Env.isDemo()) {
        return WorkatoStatus.connected;
    }

    const connection = workatoConnection as WorkatoConnection;
    const authStatus = connection.authorization_status;
    const authError =
        connection.authorization_error ||
        connection.authorization_status === 'exception';
    const connectionLostReason = connection.connection_lost_reason;
    const configured = authStatus || authError;

    if (connectionLostReason !== null && !authError)
        return WorkatoStatus.connectionError;

    if (authError) return WorkatoStatus.authorizationError;

    if (!configured) return WorkatoStatus.notConnected;

    if (authStatus === 'success' && !authError) return WorkatoStatus.connected;

    return WorkatoStatus.unknown;
};
